/**
 * The cabinet's shell.
 *
 * WHAT THIS IS AND IS NOT. M0's UI proof is "the shared `web/ui` package builds, and each of the
 * two browser apps renders ONE REAL SCREEN from the token set and the theme object, in CI"
 * (docs/design/12-build-plan.md). This is that one screen. It is not the cabinet:
 * sign-in, the article list, the editor and the payments pages live at their own addresses, and
 * this screen only points at them. What it must do is prove the wiring — the theme reaches the
 * page, the tokens reach the CSS variables, and DataTable renders with its accessibility intact.
 *
 * IT IS ALSO THE FRONT DOOR, AND IT STAYS SESSION-FREE. `/` is read by the accessibility leg and by
 * the deploy smoke check (`src/router.tsx` header), so nothing here reads the session or the API:
 * the links below are plain addresses, and the one new row in the table is a build-time fact, the
 * same kind of fact as the three already there.
 */
import { Anchor, Badge, Button, Container, Group, Stack, Text, Title } from '@mantine/core';
import { DataTable, useThemeToggle } from '@moonx/ui';
import type { DataTableColumn, DataTableFeatures } from '@moonx/ui';
import type { TFunction } from 'i18next';
import { useMemo } from 'react';
import { AUTHORS_API_BASE_URL, isLiveApiConfigured } from './config';
import { CABINET_HOME_PATH, ENTER_PATH, SIGN_IN_PATH } from './features/auth/navigation';
import { Trans, useTranslation } from './i18n';

interface ShellRow {
  /** Stable across languages: the table's row id. */
  readonly id: string;
  readonly name: string;
  readonly detail: string;
}

/** The app's own build-time facts, on purpose: invented article titles would prove nothing, while
 *  these are unreadable if the theme or the config is wrong. Drawn in the default build only. */
function shellRows(t: TFunction<'shell'>): readonly ShellRow[] {
  return [
    { id: 'host', name: t('front.host'), detail: 'authors.moonx.app' },
    { id: 'api', name: t('front.apiBase'), detail: AUTHORS_API_BASE_URL },
    { id: 'auth', name: t('front.authentication'), detail: t('front.authenticationValue') },
    // Which of the two worlds this build is in (src/config.ts, the one live flag). Said on the page
    // because the two builds otherwise look identical until a screen tries to load something.
    {
      id: 'data',
      name: t('front.data'),
      detail: isLiveApiConfigured() ? t('front.dataLive') : t('front.dataFixtures'),
    },
  ];
}

export function App() {
  const { computed, toggle } = useThemeToggle();
  const { t } = useTranslation('shell');
  const rows = useMemo(() => shellRows(t), [t]);
  const columns = useMemo(
    (): ReadonlyArray<DataTableColumn<DataTableFeatures, ShellRow>> => [
      { accessorKey: 'name', header: t('front.setting'), enableSorting: true },
      { accessorKey: 'detail', header: t('front.value'), enableSorting: false },
    ],
    [t],
  );
  // R-045: in a LIVE build `/` is what an author sees at the bare address, so it shows only the
  // heading and the ways in. The default build - the one the two gates read - is unchanged.
  const live = isLiveApiConfigured();

  return (
    <Container size="md" py="xl">
      <Stack gap="xl">
        <Group justify="space-between" align="flex-start">
          <Stack gap={4}>
            <Title order={1}>{t('front.title')}</Title>
            <Text c="dimmed" size="sm">
              {t('front.standfirst')}
            </Text>
          </Stack>
          {live ? null : (
            <Group gap="sm">
              <Badge variant="light">
                {computed === 'dark' ? t('front.dark') : t('front.light')}
              </Badge>
              <Button variant="filled" onClick={toggle}>
                {t('front.switchTheme')}
              </Button>
            </Group>
          )}
        </Group>

        {live ? null : (
          <DataTable
            caption={t('front.settingsCaption')}
            columns={columns}
            data={rows}
            getRowId={(row) => row.id}
            emptyMessage={t('front.noSettings')}
          />
        )}

        {/* The Anchor-inside-dimmed-text shape the accessibility leg passes in both schemes (the
            theme underlines every Anchor, web/ui/src/theme/theme.ts:212). Plain addresses, not
            router links: this screen needs no session and no router context. */}
        <Text size="xs" c="dimmed">
          <Trans
            t={t}
            i18nKey="front.waysIn"
            values={{ enterPath: ENTER_PATH }}
            components={{
              cabinet: <Anchor href={CABINET_HOME_PATH} size="xs" />,
              signIn: <Anchor href={SIGN_IN_PATH} size="xs" />,
            }}
          />
        </Text>
      </Stack>
    </Container>
  );
}
